import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const HomeContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
`;

const Hero = styled.section`
  text-align: center;
  padding: 4rem 2rem;
  margin-bottom: 4rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  background: linear-gradient(45deg, #00ff88, #00d4ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba(0, 255, 136, 0.5);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  color: #cccccc;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 255, 136, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 255, 136, 0.4);
    background: linear-gradient(45deg, #00cc6a, #00ff88);
  }
`;

const FeaturesSection = styled.section`
  padding: 2rem 0;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: #ffffff;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-5px);
    border-color: #00ff88;
    box-shadow: 0 10px 30px rgba(0, 255, 136, 0.2);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #00ff88;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const FeatureDescription = styled.p`
  color: #cccccc;
  line-height: 1.6;
`;

const StatsSection = styled.section`
  padding: 4rem 2rem;
  text-align: center;
  background: rgba(0, 255, 136, 0.05);
  margin: 4rem 0;
  border-radius: 15px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #00ff88;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #cccccc;
  font-size: 1.1rem;
`;

const Home = () => {
  return (
    <HomeContainer>
      <Hero>
        <HeroTitle>Skylink</HeroTitle>
        <HeroSubtitle>
          Connecting inmates with their loved ones through secure, reliable communication. 
          Create accounts, load funds, and stay connected with those who matter most.
        </HeroSubtitle>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <CTAButton to="/signin">Sign In</CTAButton>
          <CTAButton to="/signup">Get Started Today</CTAButton>
        </div>
      </Hero>

      <FeaturesSection>
        <SectionTitle>Why Choose Skylink?</SectionTitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>📞</FeatureIcon>
            <FeatureTitle>Secure Communication</FeatureTitle>
            <FeatureDescription>
              Reliable phone service designed specifically for correctional facilities 
              with advanced security features and monitoring capabilities.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>💰</FeatureIcon>
            <FeatureTitle>Easy Account Management</FeatureTitle>
            <FeatureDescription>
              Simple and secure way to load funds, check balances, and manage 
              inmate accounts from anywhere, anytime.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>🔒</FeatureIcon>
            <FeatureTitle>Privacy & Security</FeatureTitle>
            <FeatureDescription>
              Advanced encryption and security protocols ensure all communications 
              remain private and protected.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>📱</FeatureIcon>
            <FeatureTitle>Dedicated Numbers</FeatureTitle>
            <FeatureDescription>
              Each inmate receives a unique Skylink number for reliable, 
              uninterrupted communication with family and friends.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>⚡</FeatureIcon>
            <FeatureTitle>Instant Setup</FeatureTitle>
            <FeatureDescription>
              Quick and easy account creation process. Get connected 
              in minutes, not days.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>🌐</FeatureIcon>
            <FeatureTitle>24/7 Support</FeatureTitle>
            <FeatureDescription>
              Round-the-clock customer support to help with any questions 
              or issues you may have.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <StatsSection>
        <SectionTitle>Skylink by the Numbers</SectionTitle>
        <StatsGrid>
          <StatItem>
            <StatNumber>10,000+</StatNumber>
            <StatLabel>Active Users</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>500+</StatNumber>
            <StatLabel>Facilities Served</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>99.9%</StatNumber>
            <StatLabel>Uptime</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>24/7</StatNumber>
            <StatLabel>Support Available</StatLabel>
          </StatItem>
        </StatsGrid>
      </StatsSection>
    </HomeContainer>
  );
};

export default Home; 